import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { NotificationTypeEnum } from '../enums/NotificationTypeEnum';
import { FileService } from '../services/fileService';

import File from '../interfaces/File';
import FilePermissions from '../interfaces/FilePermissions';
import User from '../interfaces/User';
import { useAuth } from './useAuth';
import { useNotifications } from './useNotifications';
import useOpenFile from './useOpenFile';

const fileService = new FileService();

function useProtectedFileToken() {
  const { t } = useTranslation();
  const { createNotification } = useNotifications();
  const { login } = useAuth();
  const { openFileViewer } = useOpenFile();
  const { fileUid } = useParams<{ fileUid: string }>();
  const { token } = useParams<{ token: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Function to request and return file data based on the provided file ID.
   *
   * This function sets the loading state to true and shows a loading overlay
   * while the file request is being processed. It then attempts to retrieve the
   * file using the file service. If the request succeeds, the response is returned.
   * If the request fails, an error notification is created and the selected file
   * is set to null. Finally, the loading state is set to false.
   *
   * @param {string} fileId - The ID of the file to be requested.
   * @returns {Promise<*>} A promise that resolves to the response of the file request.
   */
  const getFilePermissions = async (fileId: string): Promise<any> => {
    setIsLoading(true);
    return fileService
      .getFilePermissions(fileId)
      .then((response: FilePermissions) => {
        if (response.fileGranted.fileLimitation.accessDenied) {
          setError(t('errors.requestFilePermissions'));
          createNotification(
            t('errors.requestFilePermissions'),
            NotificationTypeEnum.DANGER
          );
          return;
        }

        const fileInfo = response;
        // If there's no filePermission add it to the file
        if (!fileInfo.fileGranted.fileLimitation) {
          fileInfo.fileGranted.fileLimitation = {
            canDownload: false,
            canEdit: true,
            canOpenWithOffice: true,
          };
        } else {
          fileInfo.fileGranted.fileLimitation.canDownload = false;
        }

        const file: File = {
          uid: fileInfo.fileProtected.uid,
          fileId: fileInfo.fileProtected.fileId,
          author: '',
          owner: '',
          title: fileInfo.fileProtected.originaleFileName,
          description: fileInfo.fileProtected.description,
          thumbnail: '',
          fileExtension: fileInfo.fileProtected.fileExtension,
          createdAt: fileInfo.fileProtected.createdAt,
          lastModifiedAt: fileInfo.fileProtected.lastModifiedAt,
          isUnsealed: false,
          tags: [],
          tagsV2: [],
          currentPrice: 0,
          currencyPrice: '',
          isResalable: false,
          fileSize: fileInfo.fileProtected.fileSize,
          fileEditions: 0,
          isDigitallySigned: false,
          url: '',
          purchaseDate: 0,
          readOnly: false,
          expiry: 0,
          isFavourite: false,
          isArchived: false,
          isDeleted: false,
          isShared: false,
          isUnRead: false,
          isQuickShare: false,
          lastTimeOpened: 0,
          isFolder: false,
          createdBy: fileInfo.fileProtected.createdBy,
          ownerId: fileInfo.fileProtected.userId,
        };

        openFileViewer(fileInfo, file, false).then(() => {});
      })
      .catch(() => {
        setError(t('errors.requestFilePermissions'));
        createNotification(
          t('errors.requestFilePermissions'),
          NotificationTypeEnum.DANGER
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * An asynchronous function to extract PDF data using a file service, handle the extraction response,
   * and manage application state and navigation.
   *
   * The function attempts to extract PDF data associated with a given file UID and authentication token.
   * If either the UID or token is missing, the function redirects to the login page.
   * It uses a loading state indicator while performing the extraction operation and handles the following scenarios:
   * - On success: Logs the response from the file service.
   * - On failure: Creates a notification to inform the user of permission issues and resets the file.
   * - In all cases: Ensures the loading state is updated and attempts to open the file viewer with
   *   relevant file display configurations.
   *
   * Dependencies:
   * - fileUid: Unique identifier for the file to be processed.
   * - token: Authentication token required for file service requests.
   * - navigate: Function to change the application's current route.
   * - setIsLoading: State management function to track loading status.
   * - fileService: Object facilitating interaction with file-related operations.
   * - createNotification: Function to display notifications to the user.
   * - t: Function for translating error messages.
   * - setFile: State management function to reset file data.
   * - openFileViewer: Function that configures and displays file viewer with given file data.
   * - isLoggedIn: Current authentication state of the user.
   */
  const getViewer = useCallback(async () => {
    if (!fileUid || !token) {
      createNotification(
        t('errors.getViewerData'),
        NotificationTypeEnum.DANGER
      );
      return;
    }

    setIsLoading(true);

    try {
      const response = await fileService.getViewer(parseInt(fileUid), token);
      const userResponse: User = {
        clientCulture: response.ClientCulture,
        createdAt: response.CreatedAt,
        createdBy: response.CreatedBy,
        expiry: response.Expiry,
        privateUser: response.PrivateUser,
        role: response.Role,
        status: response.Status,
        tokenSignalR: response.TokenSignalR,
        userId: response.UserId,
        firstName: response.FirstName,
        email: response.Email,
        userName: response.UserName,
        lastName: response.LastName,
        token: response.Token,
        license: undefined,
        sharedFilesCount: 0,
      };

      await login(userResponse, false);
      await getFilePermissions(fileUid);
    } catch (error) {
      setError(t('errors.getViewerData'));
      createNotification(
        t('errors.getViewerData'),
        NotificationTypeEnum.DANGER
      );
    } finally {
      setIsLoading(false);
    }
  }, [fileUid, createNotification, t]);

  useEffect(() => {
    Promise.all([getViewer()]).then(() => {});
  }, [getViewer]);

  return {
    isLoading,
    error,
    t,
  };
}

export default useProtectedFileToken;
